import React, { useState, useEffect, useCallback } from 'react';
import Dashboard    from './Dashboard';
import Movimientos  from './Movimientos';
import LibroContable from './LibroContable';
import IvaPanel     from './IvaPanel';
import { SEED_DATA } from './seedData';

const STORAGE_KEY = 'contabilidad_v2';

const TABS = [
  { id:'dashboard',   label:'Dashboard',      icon:'ti-layout-dashboard' },
  { id:'movimientos', label:'Movimientos',     icon:'ti-list'             },
  { id:'libro_ventas',label:'Libro Ventas',    icon:'ti-file-invoice'     },
  { id:'libro_compras',label:'Libro Compras',  icon:'ti-shopping-cart'    },
  { id:'iva',         label:'IVA / F29',       icon:'ti-percentage'       },
];

function Toast({ msg, ok, onDone }) {
  useEffect(()=>{ const t=setTimeout(onDone,2800); return ()=>clearTimeout(t); },[onDone]);
  return (
    <div style={{
      position:'fixed',top:18,right:18,zIndex:999,
      background: ok?'rgba(16,185,129,0.15)':'rgba(239,68,68,0.15)',
      color: ok?'var(--green)':'var(--red)',
      border:`1px solid ${ok?'var(--green-border)':'var(--red-border)'}`,
      borderRadius:'var(--radius)',padding:'10px 18px',fontSize:13,fontWeight:500,
      display:'flex',alignItems:'center',gap:8,boxShadow:'0 4px 24px rgba(0,0,0,0.4)',
    }}>
      <i className={`ti ${ok?'ti-check':'ti-x'}`} />
      {msg}
    </div>
  );
}

export default function App() {
  const [movimientos, setMovimientos] = useState([]);
  const [tab,  setTab]   = useState('dashboard');
  const [toast, setToast] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [seeded, setSeeded] = useState(false);

  /* ── cargar desde localStorage ── */
  useEffect(()=>{
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        setMovimientos(parsed.data || []);
        setSeeded(parsed.seeded || false);
      }
    } catch {}
    setLoaded(true);
  },[]);

  const persist = useCallback((data, sd) => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify({data, seeded:sd})); } catch {}
  },[]);

  const notify = (msg, ok=true) => setToast({msg,ok});

  /* ── cargar datos semilla ── */
  const handleLoadSeed = useCallback(()=>{
    const combined = [...SEED_DATA];
    setMovimientos(combined);
    setSeeded(true);
    persist(combined, true);
    notify(`✓ ${combined.length} registros cargados desde COMERCIAL.xlsx`);
  },[persist]);

  /* ── CRUD ── */
  const handleSave = useCallback((data, editId)=>{
    setMovimientos(prev=>{
      const updated = editId
        ? prev.map(m => m.id===editId ? {...data,id:editId} : m)
        : [{...data, id:Date.now()}, ...prev];
      persist(updated, seeded);
      return updated;
    });
    notify(editId ? 'Movimiento actualizado' : 'Movimiento registrado');
  },[persist, seeded]);

  const handleDelete = useCallback((id)=>{
    setMovimientos(prev=>{
      const updated = prev.filter(m=>m.id!==id);
      persist(updated, seeded);
      return updated;
    });
    notify('Movimiento eliminado');
  },[persist, seeded]);

  /* ── importar CSV ── */
  const handleImportCSV = useCallback((e)=>{
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const text = ev.target.result;
        const lines = text.split('\n').filter(l=>l.trim());
        const headers = lines[0].split(',').map(h=>h.replace(/^"|"$/g,'').trim());
        const imported = lines.slice(1).map((line, idx)=>{
          const vals = line.match(/(".*?"|[^,]+)/g) || [];
          const clean = v => String(v||'').replace(/^"|"$/g,'').trim();
          const obj = {};
          headers.forEach((h,i)=>{ obj[h]=clean(vals[i]); });
          return {
            id: Date.now()+idx,
            tipo: obj['Tipo']||'compra',
            subTipo: obj['SubTipo']||'nacional',
            categoria: obj['Categoría']||obj['Categoria']||'Otros egresos',
            descripcion: obj['Descripción']||obj['Descripcion']||'',
            monto: parseFloat(obj['Monto']||0),
            monto_iva: parseFloat(obj['IVA']||0),
            monto_total: parseFloat(obj['Total']||0)||parseFloat(obj['Monto']||0),
            fecha: obj['Fecha']||today(),
            notas: obj['Notas']||'',
            rutContraparte: obj['RUT']||'',
            razonSocial: obj['Razón Social']||obj['Razon Social']||'',
            nFactura: obj['N° Factura']||'',
            pn: obj['P/N']||'',
            clienteFinal: obj['Cliente Final']||'',
            tipoDoc: obj['Tipo Doc']||'FAC',
          };
        }).filter(r=>r.monto>0);
        setMovimientos(prev=>{
          const updated=[...prev,...imported];
          persist(updated, seeded);
          return updated;
        });
        notify(`✓ ${imported.length} registros importados desde CSV`);
      } catch(err) {
        notify('Error al leer el CSV', false);
      }
    };
    reader.readAsText(file, 'UTF-8');
    e.target.value='';
  },[persist, seeded]);

  /* ── exportar CSV ── */
  const handleExport = ()=>{
    const h=['Tipo','SubTipo','Categoría','Descripción','Monto','IVA','Total','Fecha','RUT','Razón Social','N° Factura','P/N','Cliente Final','Tipo Doc','Notas'];
    const rows = movimientos.map(m=>[
      m.tipo,m.subTipo||'',m.categoria,m.descripcion||'',m.monto,
      m.monto_iva||0,m.monto_total||m.monto,m.fecha,
      m.rutContraparte||'',m.razonSocial||'',m.nFactura||'',
      m.pn||'',m.clienteFinal||'',m.tipoDoc||'',m.notas||'',
    ]);
    const csv=[h,...rows].map(r=>r.map(c=>`"${String(c).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob=new Blob(['﻿'+csv],{type:'text/csv;charset=utf-8;'});
    const url=URL.createObjectURL(blob);
    const a=document.createElement('a');
    a.href=url; a.download=`contabilidad_${new Date().toISOString().split('T')[0]}.csv`;
    a.click(); URL.revokeObjectURL(url);
    notify('CSV exportado');
  };

  if (!loaded) return (
    <div style={{display:'flex',alignItems:'center',justifyContent:'center',minHeight:'100vh',flexDirection:'column',gap:12,color:'var(--text2)'}}>
      <i className="ti ti-loader-2" style={{fontSize:32,animation:'spin 1s linear infinite'}} />
      <p>Cargando...</p>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <div style={{display:'flex',minHeight:'100vh'}}>
      {/* ── Sidebar ── */}
      <aside style={{
        width:220,flexShrink:0,background:'var(--bg2)',
        borderRight:'1px solid var(--border)',padding:'1.25rem 0.75rem',
        display:'flex',flexDirection:'column',gap:2,
        position:'sticky',top:0,height:'100vh',overflowY:'auto',
      }}>
        {/* Logo */}
        <div style={{marginBottom:24,paddingLeft:8}}>
          <div style={{display:'flex',alignItems:'center',gap:9}}>
            <div style={{width:34,height:34,borderRadius:9,background:'linear-gradient(135deg,#3b82f6,#8b5cf6)',display:'flex',alignItems:'center',justifyContent:'center'}}>
              <i className="ti ti-report-money" style={{fontSize:18,color:'#fff'}} />
            </div>
            <div>
              <p style={{fontWeight:700,fontSize:13,lineHeight:1.2}}>Contabilidad</p>
              <p style={{fontSize:10,color:'var(--text3)'}}>Inmaluca</p>
            </div>
          </div>
        </div>

        {/* Nav */}
        {TABS.map(t=>(
          <button key={t.id} onClick={()=>setTab(t.id)} style={{
            width:'100%',textAlign:'left',justifyContent:'flex-start',
            padding:'9px 12px',border:'none',
            background: tab===t.id ? 'var(--blue-bg)':'transparent',
            color: tab===t.id ? 'var(--blue)':'var(--text2)',
            fontWeight: tab===t.id ? 600:400,
            borderRadius:'var(--radius-sm)',
          }}>
            <i className={`ti ${t.icon}`} style={{fontSize:16}} />
            {t.label}
          </button>
        ))}

        {/* Acciones */}
        <div style={{marginTop:'auto',display:'flex',flexDirection:'column',gap:5,paddingTop:12,borderTop:'1px solid var(--border)'}}>
          {!seeded && (
            <button onClick={handleLoadSeed} style={{
              width:'100%',justifyContent:'center',fontSize:12,
              background:'rgba(245,158,11,0.12)',color:'var(--amber)',
              borderColor:'rgba(245,158,11,0.3)',
            }}>
              <i className="ti ti-database-import" /> Cargar datos
            </button>
          )}
          <label style={{
            width:'100%',display:'flex',alignItems:'center',justifyContent:'center',gap:6,
            padding:'8px 16px',fontSize:12,cursor:'pointer',
            background:'transparent',color:'var(--text2)',
            border:'1px solid var(--border2)',borderRadius:'var(--radius-sm)',fontWeight:500,
          }}>
            <i className="ti ti-upload" /> Importar CSV
            <input type="file" accept=".csv" style={{display:'none'}} onChange={handleImportCSV} />
          </label>
          <button onClick={handleExport} style={{width:'100%',justifyContent:'center',fontSize:12}}>
            <i className="ti ti-download" /> Exportar CSV
          </button>
          <p style={{fontSize:10,color:'var(--text3)',textAlign:'center',paddingTop:4}}>
            {movimientos.length} registros
          </p>
        </div>
      </aside>

      {/* ── Main ── */}
      <main style={{flex:1,padding:'1.75rem 2rem',maxWidth:1100,width:'100%',overflow:'auto'}}>
        {tab==='dashboard'    && <Dashboard    movimientos={movimientos} onTab={setTab} />}
        {tab==='movimientos'  && <Movimientos  movimientos={movimientos} onSave={handleSave} onDelete={handleDelete} />}
        {tab==='libro_ventas' && <LibroContable movimientos={movimientos} mode="ventas"  />}
        {tab==='libro_compras'&& <LibroContable movimientos={movimientos} mode="compras" />}
        {tab==='iva'          && <IvaPanel      movimientos={movimientos} />}
      </main>

      {toast && <Toast msg={toast.msg} ok={toast.ok} onDone={()=>setToast(null)} />}
    </div>
  );
}
